
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from '@/contexts/AuthContext';
import { Award, Store, AlertTriangle, CheckCircle, ShoppingCart, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Product } from '@/types/product';
import { Store as StoreType } from '@/types';
import { validateSufficientPoints, processPointsPurchase } from '@/services/storePointService';
import { useNavigate } from 'react-router-dom';
import { useFavorites } from '@/hooks/useFavorites';
import { useLanguage } from '@/contexts/LanguageContext';

interface ProductCheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product | null;
  quantity?: number;
  onSuccess: () => void;
}

interface UserStore {
  id: string;
  store_id: string;
  is_default: boolean;
}

const ProductCheckoutModal: React.FC<ProductCheckoutModalProps> = ({
  isOpen,
  onClose,
  product,
  quantity = 1,
  onSuccess
}) => {
  const navigate = useNavigate();
  const { user, userProfile, refreshUserProfile } = useAuth();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { t } = useLanguage();
  const [processing, setProcessing] = useState(false);
  const [exchangeSuccess, setExchangeSuccess] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState<string>('');
  const [userStores, setUserStores] = useState<UserStore[]>([]);
  const [availableStores, setAvailableStores] = useState<StoreType[]>([]);
  const [loadingStores, setLoadingStores] = useState(true);
  const [currentPoints, setCurrentPoints] = useState(0);

  const totalPoints = product ? product.pointsPrice * quantity : 0;

  // Resetar estado quando o modal abrir/fechar
  useEffect(() => {
    if (isOpen) {
      setExchangeSuccess(false);
    }
  }, [isOpen]);

  // Carregar lojas do usuário e lojas disponíveis
  useEffect(() => {
    const loadStores = async () => {
      if (!user || !isOpen) return;

      try {
        setLoadingStores(true);

        // Carregar lojas do usuário
        const { data: userStoresData, error: userStoresError } = await supabase
          .from('user_stores')
          .select('id, store_id, is_default')
          .eq('user_id', user.id);

        if (userStoresError) throw userStoresError;

        // Carregar todas as lojas disponíveis
        const { data: allStoresData, error: allStoresError } = await supabase
          .from('stores')
          .select('id, name, location');

        if (allStoresError) throw allStoresError;

        setUserStores(userStoresData || []);
        setAvailableStores(allStoresData || []);

        // Selecionar loja padrão automaticamente
        const defaultStore = userStoresData?.find(s => s.is_default);
        if (defaultStore) {
          setSelectedStoreId(defaultStore.store_id);
        } else if (userStoresData && userStoresData.length > 0) {
          setSelectedStoreId(userStoresData[0].store_id);
        }

      } catch (error) {
        console.error('Erro ao carregar lojas:', error);
        toast.error(t('productCheckout.errorLoadingStores'));
      } finally {
        setLoadingStores(false);
      }
    };

    loadStores();
  }, [user, isOpen]);

  // Buscar storepoints atualizados do usuário quando o modal abrir
  useEffect(() => {
    const fetchUserPoints = async () => {
      if (isOpen && user && userProfile?.connectedAccountId) {
        try {
          const { data, error } = await supabase
            .from('profile_safe')
            .select('points')
            .eq('id', user.id)
            .single();
            
          if (error) throw error;
          
          if (data) {
            setCurrentPoints(data.points || 0);
          }
        } catch (error) {
          console.error('Erro ao buscar storepoints:', error);
        }
      }
    };
    
    fetchUserPoints();
  }, [isOpen, user, userProfile]);

  const getStoreName = (storeId: string) => {
    const store = availableStores.find(s => s.id === storeId);
    return store?.name || t('productCheckout.storeNotFound');
  };

  const getStoreLocation = (storeId: string) => {
    const store = availableStores.find(s => s.id === storeId);
    return store?.location || "";
  };

  const handleCheckout = async () => {
    if (!user || !userProfile?.connectedAccountId || !product || !selectedStoreId) {
      toast.error(t('productCheckout.incompleteData'));
      return;
    }

    if (currentPoints < totalPoints) {
      toast.error(t('productCheckout.insufficientPoints'));
      return;
    }

    if (userStores.length === 0) {
      toast.error(t('productCheckout.selectStoreRequired'));
      return;
    }

    setProcessing(true);

    try {

      // Validação com sincronização forçada antes de processar
      const validation = await validateSufficientPoints(user.id, totalPoints, true);
      if (!validation.valid) {
        toast.error(validation.message || t('productCheckout.insufficientBalance'));
        return;
      }

      // Processar a compra com os storepoints usando o serviço real
      const result = await processPointsPurchase(user.id, product.id, product.pointsPrice, undefined, selectedStoreId);

      if (result.success) {
        // Remover dos favoritos se estiver lá
        if (product && isFavorite(product.id)) {
          await toggleFavorite(product.id);
        }

        // Atualizar o perfil do usuário
        await refreshUserProfile();

        
        // Mostrar tela de sucesso
        setExchangeSuccess(true);
        
        // Chamar onSuccess para remover do carrinho
        onSuccess();
      }

    } catch (error: any) {
      // Exchange processing error
      toast.error(error.message || t('productCheckout.exchangeError'));
    } finally {
      setProcessing(false);
    }
  };

  const handleContinueShopping = () => {
    onClose();
    navigate('/checkout');
  };

  const handleBackToStore = () => {
    onClose();
    navigate('/shop');
  };

  const handleCloseSuccess = () => {
    setExchangeSuccess(false);
    onClose();
  };

  if (!product) return null;

  const canProceed = selectedStoreId && currentPoints >= totalPoints && userStores.length > 0;

  // Tela de sucesso
  if (exchangeSuccess) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => {
        if (!open) handleCloseSuccess();
      }}>
        <DialogContent className="bg-brand-gray border-none text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl text-center">
              <CheckCircle className="text-green-500" size={24} />
              {t('productCheckout.successTitle')}
            </DialogTitle>
            <DialogDescription className="text-gray-400 text-center">
              {t('productCheckout.successDescription')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Produto trocado */}
            <Card className="bg-brand-dark border-gray-700">
              <CardContent className="p-4">
                <div className="flex gap-4 items-center">
                  <div className="w-16 h-16 bg-white rounded flex-shrink-0">
                    <img 
                      src={product.image} 
                      alt={product.name} 
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="flex-grow">
                    <h3 className="font-medium">{product.name}</h3>
                    <p className="text-sm text-gray-400 mt-1">{t('productCheckout.quantity')}: {quantity}</p>
                    <p className="text-brand-red font-bold mt-1">
                      {totalPoints} {t('productCheckout.storepointsExchanged')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Loja de retirada */}
            <Card className="bg-brand-dark border-gray-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Store className="text-brand-red" size={16} />
                  <span className="text-sm font-medium">{t('productCheckout.pickupLocation')}</span>
                </div>
                <div>
                  <p className="font-medium">{getStoreName(selectedStoreId)}</p>
                  {getStoreLocation(selectedStoreId) && (
                    <p className="text-sm text-gray-400">{getStoreLocation(selectedStoreId)}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              onClick={handleContinueShopping}
              variant="outline" 
              className="bg-transparent border-gray-700 text-white hover:bg-gray-700 flex-1"
            >
              <ShoppingCart size={16} className="mr-2" />
              {t('productCheckout.exchangeMoreProducts')}
            </Button>
            <Button 
              onClick={handleBackToStore}
              className="bg-brand-red hover:bg-brand-red/90 flex-1"
            >
              <ArrowLeft size={16} className="mr-2" />
              {t('productCheckout.backToStore')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Tela de confirmação da troca
  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) onClose();
    }}>
      <DialogContent className="bg-brand-gray border-none text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Award className="text-brand-red" size={20} />
            {t('productCheckout.exchangePoints')}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            {t('productCheckout.confirmExchangeDetails')}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-6">
          {/* Produto */}
          <Card className="bg-brand-dark border-gray-700">
            <CardContent className="p-4">
              <div className="flex gap-4">
                <div className="w-16 h-16 bg-white rounded flex-shrink-0">
                  <img 
                    src={product.image} 
                    alt={product.name} 
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="flex-grow">
                  <h3 className="font-medium">{product.name}</h3>
                  <p className="text-sm text-gray-400 mt-1">Quantidade: {quantity}</p>
                  <p className="text-brand-red font-bold mt-2">
                    {totalPoints} storepoints
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Saldo de storepoints */}
          <Card className="bg-brand-dark border-gray-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400">{t('productCheckout.currentBalance')}</span>
                <span className="font-bold text-lg">{currentPoints} storepoints</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-400">{t('productCheckout.balanceAfterExchange')}</span>
                <span className="font-bold text-lg">
                  {Math.max(0, currentPoints - totalPoints)} storepoints
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Seleção de loja */}
          <div className="space-y-3">
            <label className="text-sm font-medium flex items-center gap-2">
              <Store size={16} />
              {t('productCheckout.deliveryLocation')}
            </label>
            
            {loadingStores ? (
              <div className="flex justify-center p-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-red"></div>
              </div>
            ) : userStores.length === 0 ? (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <AlertTriangle className="text-yellow-400 mb-2" size={20} />
                <p className="text-sm text-yellow-400">
                  {t('productCheckout.storeSelectionRequired')}
                </p>
              </div>
            ) : (
              <Select value={selectedStoreId} onValueChange={setSelectedStoreId}>
                <SelectTrigger className="bg-brand-darker border-gray-700">
                  <SelectValue placeholder={t('productCheckout.selectStore')} />
                </SelectTrigger>
                <SelectContent className="bg-brand-gray border-gray-700">
                  {userStores.map((userStore) => {
                    const storeName = getStoreName(userStore.store_id);
                    const storeLocation = getStoreLocation(userStore.store_id);
                    
                    return (
                      <SelectItem key={userStore.id} value={userStore.store_id}>
                        <div className="flex flex-col">
                          <span>{storeName}</span>
                          {storeLocation && (
                            <span className="text-xs text-gray-400">{storeLocation}</span>
                          )}
                          {userStore.is_default && (
                            <span className="text-xs text-brand-red">{t('productCheckout.defaultStore')}</span>
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Avisos */}
          {currentPoints < totalPoints && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex gap-2">
              <AlertTriangle className="text-red-400 flex-shrink-0" size={20} />
              <p className="text-sm text-red-400">
                {t('productCheckout.insufficientPointsWarning')}
              </p>
            </div>
          )}

          {!userProfile?.connectedAccountId && (
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 flex gap-2">
              <AlertTriangle className="text-orange-400 flex-shrink-0" size={20} />
              <p className="text-sm text-orange-400">
                {t('productCheckout.connectAccountRequired')}
              </p>
            </div>
          )}
        </div>
        
        <DialogFooter className="gap-2">
          <Button 
            onClick={onClose} 
            variant="outline" 
            className="bg-transparent border-gray-700 text-white hover:bg-gray-700"
          >
            {t('productCheckout.cancel')}
          </Button>
          <Button 
            onClick={handleCheckout} 
            className="bg-brand-red hover:bg-brand-red/90"
            disabled={processing || !canProceed}
          >
            {processing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t('productCheckout.processing')}
              </>
            ) : (
              <>
                <CheckCircle size={16} className="mr-2" />
                {t('productCheckout.confirmExchange')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProductCheckoutModal;
